import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { platformApi, api } from '../../services/api';
import { Btn, C } from '../../components/UI';

const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 14px',
    background: '#111',
    border: '1px solid #333',
    borderRadius: 8,
    color: '#fff',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = { display: 'block', color: '#aaa', fontSize: 12, fontWeight: 500, marginBottom: 6 };

type Screen = 'login' | 'mfa' | 'forgot' | 'forgotSent';

export default function PlatformLoginPage() {
    const { login } = usePlatformAuth();
    const nav = useNavigate();
    const [screen, setScreen] = useState<Screen>('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [pendingToken, setPendingToken] = useState('');
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const resp = await platformApi.login(email, password);
            // Platform admins always require MFA (highest-privilege account in the system) — the
            // password step never returns a token directly, only a pending-verification handle.
            if (resp.mfaRequired && resp.pendingToken) {
                setPendingToken(resp.pendingToken);
                setScreen('mfa');
            } else if (resp.token && resp.userId && resp.name && resp.email) {
                login({ token: resp.token, userId: resp.userId, name: resp.name, email: resp.email });
                nav('/platform');
            } else {
                setError('Login failed. Please try again.');
            }
        } catch (err: any) {
            setError(err.message || 'Invalid credentials.');
        } finally {
            setLoading(false);
        }
    };

    const handleMfa = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const resp = await api.verifyMfa(pendingToken, otp);
            login({ token: resp.token, userId: resp.userId, name: resp.name, email: resp.email });
            nav('/platform');
        } catch (err: any) {
            setError(err.message || 'Invalid or expired code.');
        } finally {
            setLoading(false);
        }
    };

    const handleForgot = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await api.forgotPassword(email);
            setScreen('forgotSent');
        } catch (err: any) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#111',
            fontFamily: "'Inter', sans-serif",
        }}>
            <div style={{
                background: '#1a1a1a',
                border: '1px solid #2a2a2a',
                borderRadius: 16,
                padding: '40px 48px',
                width: '100%',
                maxWidth: 420,
            }}>
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        background: C.greenMid,
                        borderRadius: 12,
                        marginBottom: 16,
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                                stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 700, margin: 0 }}>Platform Admin</h1>
                    <p style={{ color: '#888', fontSize: 13, marginTop: 6 }}>
                        {screen === 'login' && 'ServiceTrek internal portal'}
                        {screen === 'mfa' && `Enter the code sent to ${email}`}
                        {screen === 'forgot' && 'Reset your password'}
                        {screen === 'forgotSent' && 'Check your email'}
                    </p>
                </div>

                {screen === 'login' && (
                    <form onSubmit={handleSubmit}>
                        <div style={{ marginBottom: 16 }}>
                            <label style={labelStyle}>Email</label>
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus style={inputStyle} />
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label style={labelStyle}>Password</label>
                            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required style={inputStyle} />
                        </div>
                        <div style={{ textAlign: 'right', marginBottom: 20 }}>
                            <button type="button" onClick={() => { setError(''); setScreen('forgot'); }}
                                style={{ background: 'none', border: 'none', color: C.greenMid, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                                Forgot password?
                            </button>
                        </div>
                        {error && <ErrorBox message={error} />}
                        <Btn type="submit" fullWidth disabled={loading}>{loading ? 'Signing in...' : 'Sign in'}</Btn>
                    </form>
                )}

                {screen === 'mfa' && (
                    <form onSubmit={handleMfa}>
                        <div style={{ marginBottom: 20 }}>
                            <label style={labelStyle}>Verification code</label>
                            <input
                                type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                                value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))} required autoFocus
                                placeholder="6-digit code"
                                style={{ ...inputStyle, letterSpacing: 6, fontSize: 18, textAlign: 'center' }}
                            />
                        </div>
                        {error && <ErrorBox message={error} />}
                        <Btn type="submit" fullWidth disabled={loading || otp.length !== 6}>{loading ? 'Verifying...' : 'Verify'}</Btn>
                        <button type="button" onClick={() => { setError(''); setOtp(''); setScreen('login'); }}
                            style={{ display: 'block', margin: '16px auto 0', background: 'none', border: 'none', color: '#888', fontSize: 12, cursor: 'pointer' }}>
                            ← Back to sign in
                        </button>
                    </form>
                )}

                {screen === 'forgot' && (
                    <form onSubmit={handleForgot}>
                        <div style={{ marginBottom: 20 }}>
                            <label style={labelStyle}>Email</label>
                            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus style={inputStyle} />
                        </div>
                        {error && <ErrorBox message={error} />}
                        <Btn type="submit" fullWidth disabled={loading}>{loading ? 'Sending...' : 'Send reset link'}</Btn>
                        <button type="button" onClick={() => { setError(''); setScreen('login'); }}
                            style={{ display: 'block', margin: '16px auto 0', background: 'none', border: 'none', color: '#888', fontSize: 12, cursor: 'pointer' }}>
                            ← Back to sign in
                        </button>
                    </form>
                )}

                {screen === 'forgotSent' && (
                    <div style={{ textAlign: 'center' }}>
                        <p style={{ color: '#ccc', fontSize: 13.5, lineHeight: 1.6, marginBottom: 24 }}>
                            If an account exists for <strong style={{ color: '#fff' }}>{email}</strong>, a password reset
                            link is on its way. It expires in 1 hour.
                        </p>
                        <Btn fullWidth variant="outline" onClick={() => { setError(''); setScreen('login'); }}>Back to sign in</Btn>
                    </div>
                )}
            </div>
        </div>
    );
}

function ErrorBox({ message }: { message: string }) {
    return (
        <div style={{
            background: '#2a1010',
            border: '1px solid #5a2020',
            color: '#f87171',
            padding: '10px 14px',
            borderRadius: 8,
            fontSize: 13,
            marginBottom: 16,
        }}>
            {message}
        </div>
    );
}
